/** Post-submit comparison — human vs Ace (admin calibration). */

import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleXmark,
  faRobot,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import { AceFreshRunPanel } from "./AceFreshRunPanel";
import { AceAgreedWithYouBadge, AceDeferredReason } from "./AceDeferredBadge";
import { aceTrialRunBadge } from "./aceTrialLabels";
import { aceCheckDeferredToHuman } from "./aceDeferredToHuman";
import type {
  AcePhaseRating,
  AceProfile,
  CheckAnswer,
  CheckAnswers,
  FaultPickerOutcome,
  InjectedFaultReport,
  PhaseRatings,
  Rag,
  ReviewCaseContext,
  ReviewComparison,
  ReviewPhase,
} from "./types";
import { ragOption } from "./RagOption";

interface AceComparisonPanelProps {
  comparison: ReviewComparison | null;
  loading: boolean;
  error: string | null;
  reviewId?: string | null;
  catalogueKey?: string | null;
  aceTrialRefreshToken?: number;
  /** Live wizard answers — used when the stored review is missing per-check rows. */
  humanPhaseRatings?: PhaseRatings | null;
  onRunAce: () => void;
  onRerunAce?: () => void;
  onRunFreshAce?: (args: {
    criteriaVersionId: string | null;
    aceProfile: AceProfile;
    note: string;
    promote: boolean;
  }) => void;
  onSelectAceTrial?: (trialId: string) => void;
  onPromoteAceTrial?: (trialId: string) => void;
  focusCheckId?: string | null;
}

const PHASES: { key: ReviewPhase; label: string }[] = [
  { key: "orchestration", label: "Orchestrate" },
  { key: "output", label: "Output" },
];

function answerLabel(answer: CheckAnswer): string {
  if (answer === "pass") return "Pass";
  if (answer === "fail") return "Fail";
  return "N/A";
}

function ragLabel(rag: Rag | null): string {
  if (!rag) return "Not rated";
  return ragOption(rag).label;
}

function phaseSummary(phase: AcePhaseRating | undefined): string[] {
  if (!phase) return [];
  if (phase.summary?.length) return phase.summary;
  return phase.note?.trim() ? [phase.note.trim()] : [];
}

function humanRagForPhase(comparison: ReviewComparison, phase: ReviewPhase): Rag | null {
  return comparison.human_ratings.find((r) => r.step_type === phase)?.human_rag ?? null;
}

function aceRagForPhase(comparison: ReviewComparison, phase: ReviewPhase): Rag | null {
  const ace = comparison.ace_rating;
  if (!ace) return null;
  return phase === "orchestration" ? ace.orchestration.suggested_rag : ace.output.suggested_rag;
}

function checksByPhase(
  comparison: ReviewComparison,
  phase: ReviewPhase,
  fallbackAnswers?: CheckAnswers | null,
) {
  const criteria = comparison.criteria[phase];
  const human = comparison.human_ratings.find((r) => r.step_type === phase);
  const humanById = Object.fromEntries((human?.checks ?? []).map((c) => [c.id, c]));
  const acePhase = phase === "orchestration" ? comparison.ace_rating?.orchestration : comparison.ace_rating?.output;
  const aceById = Object.fromEntries((acePhase?.checks ?? []).map((c) => [c.id, c]));

  return criteria.checks.map((check) => {
    const ace = aceById[check.id];
    const humanCheck = humanById[check.id];
    const humanAnswer = humanCheck?.answer ?? fallbackAnswers?.[check.id] ?? null;
    return {
      check,
      human: humanAnswer,
      humanComment: humanCheck?.comment?.trim() ?? "",
      ace,
      disagrees: Boolean(ace && humanAnswer && humanAnswer !== ace.answer),
      aceFailed: Boolean(ace && ace.answer === "fail"),
    };
  });
}

function DetectionBadge({ outcome, label }: { outcome: FaultPickerOutcome; label: string }) {
  const tone =
    outcome === "detected"
      ? "text-emerald-300 bg-emerald-500/10 ring-emerald-500/25"
      : outcome === "missed"
        ? "text-rose-300 bg-rose-500/10 ring-rose-500/25"
        : "text-slate-400 bg-white/[0.03] ring-white/10";

  const text =
    outcome === "detected"
      ? "Caught it"
      : outcome === "missed"
        ? "Missed it"
        : outcome === "not_rated"
          ? "Not rated"
          : "N/A";

  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] ring-1 ${tone}`}>
      <span className="text-slate-500 mr-1">{label}</span>
      {text}
    </span>
  );
}

function InjectedFaultRow({ fault }: { fault: InjectedFaultReport }) {
  return (
    <div className="rounded-lg bg-black/20 px-3 py-3 ring-1 ring-white/5 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-100">{fault.title}</p>
        <div className="flex flex-wrap gap-1.5">
          <DetectionBadge outcome={fault.detection.human} label="You" />
          <DetectionBadge outcome={fault.detection.ace} label="Ace" />
        </div>
      </div>
      <p className="text-xs text-slate-400 leading-relaxed">{fault.description}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
        {fault.expected_rag ? (
          <span>
            Expected: <span className="text-slate-300">{ragLabel(fault.expected_rag)}</span>
          </span>
        ) : null}
        {fault.expected_hint ? <span>{fault.expected_hint}</span> : null}
        {fault.artifact_title ? (
          <span>
            Letter: <span className="text-slate-300">{fault.artifact_title}</span>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CaseContextCard({ context }: { context: ReviewCaseContext }) {
  const faults = context.injected_faults ?? [];
  const missedByHuman = faults.filter((fault) => fault.detection.human === "missed").length;
  const missedByAce = faults.filter((fault) => fault.detection.ace === "missed").length;

  return (
    <div className="rounded-lg bg-indigo-500/[0.06] px-3 py-3 ring-1 ring-indigo-500/20 space-y-3">
      <div className="space-y-1">
        <p className="text-xs font-medium text-indigo-200 uppercase tracking-wide">About this case</p>
        <p className="text-sm text-slate-100">{context.label}</p>
      </div>

      {context.source === "seed" ? (
        <div className="space-y-3">
          {faults.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Injected faults ({faults.length})
              </p>
              {faults.map((fault) => (
                <InjectedFaultRow key={fault.fault_id} fault={fault} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500">No structured fault metadata for this sample.</p>
          )}

          {(missedByHuman > 0 || missedByAce > 0) && (
            <p className="text-xs text-amber-200/90 leading-relaxed">
              {missedByHuman > 0 ? `You missed ${missedByHuman} injected fault${missedByHuman === 1 ? "" : "s"}. ` : ""}
              {missedByAce > 0 ? `Ace missed ${missedByAce}.` : ""}
            </p>
          )}

          {context.based_on_run_id ? (
            <p className="text-[11px] text-slate-500">
              <span className="text-slate-600">Cloned from real run:</span>{" "}
              <span className="font-mono text-slate-400">{context.based_on_run_id}</span>
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-1 text-xs text-slate-400">
          {context.task_name ? (
            <p>
              <span className="text-slate-500">Task:</span> {context.task_name}
            </p>
          ) : null}
          {context.interaction_id ? (
            <p>
              <span className="text-slate-500">Interaction:</span>{" "}
              <span className="font-mono text-slate-400">{context.interaction_id}</span>
            </p>
          ) : null}
          {context.client_id ? (
            <p>
              <span className="text-slate-500">Client:</span> {context.client_id}
            </p>
          ) : null}
          {context.completed_at ? (
            <p>
              <span className="text-slate-500">Completed:</span>{" "}
              {new Date(context.completed_at).toLocaleString()}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PhaseVerdictLine({
  phaseLabel,
  human,
  ace,
}: {
  phaseLabel: string;
  human: Rag | null;
  ace: Rag | null;
}) {
  if (!human && !ace) return null;

  if (!human || !ace) {
    return (
      <p className="text-sm text-slate-200">
        <span className="text-slate-400">{phaseLabel}:</span>{" "}
        {human ? (
          <>
            You rated <span className="font-medium">{ragLabel(human)}</span>
          </>
        ) : null}
        {human && !ace ? <span className="text-slate-500"> · Ace not rated</span> : null}
        {!human && ace ? (
          <>
            Ace rated <span className="font-medium">{ragLabel(ace)}</span>
          </>
        ) : null}
      </p>
    );
  }

  const same = human === ace;
  return (
    <p className="text-sm text-slate-200">
      <span className="text-slate-400">{phaseLabel}:</span>{" "}
      {same ? (
        <>
          You and Ace both rated <span className="font-medium">{ragLabel(ace)}</span>
          <FontAwesomeIcon icon={faCircleCheck} className="ml-2 text-emerald-400 text-xs" />
        </>
      ) : (
        <>
          You rated <span className="font-medium">{ragLabel(human)}</span>
          {" · "}
          Ace rated <span className="font-medium">{ragLabel(ace)}</span>
          <FontAwesomeIcon icon={faCircleXmark} className="ml-2 text-amber-400 text-xs" />
        </>
      )}
    </p>
  );
}

function DisagreementRow({
  checkLabel,
  human,
  humanComment,
  aceReason,
  aceAnswer,
  deferred,
}: {
  checkLabel: string;
  human: CheckAnswer | null;
  humanComment?: string;
  aceReason?: string;
  aceAnswer: CheckAnswer;
  deferred?: boolean;
}) {
  return (
    <div className="rounded-lg bg-amber-500/[0.06] px-3 py-3 ring-1 ring-amber-500/25 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-slate-100">{checkLabel}</p>
        {deferred ? <AceAgreedWithYouBadge compact /> : null}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span className="text-slate-400">
          You:{" "}
          <span className="text-slate-200 font-medium">{human ? answerLabel(human) : "—"}</span>
        </span>
        <span className="text-slate-400">
          Ace: <span className="text-slate-200 font-medium">{answerLabel(aceAnswer)}</span>
          {deferred ? <span className="text-slate-500"> (mirrored)</span> : null}
        </span>
      </div>
      {humanComment ? (
        <p className="text-xs text-slate-400 leading-relaxed">You: {humanComment}</p>
      ) : null}
      {aceReason ? (
        deferred ? (
          <div className="text-xs">
            <AceDeferredReason reason={aceReason} />
          </div>
        ) : (
          <p className="text-xs text-slate-400 leading-relaxed">Ace: {aceReason}</p>
        )
      ) : null}
    </div>
  );
}

function AceReasoningSection({
  phaseLabel,
  rows,
  summary,
  defaultOpen = false,
}: {
  phaseLabel: string;
  rows: ReturnType<typeof checksByPhase>;
  summary: string[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const interesting = rows.filter((r) => r.ace && (r.ace.answer === "fail" || r.disagrees));

  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  if (interesting.length === 0 && summary.length === 0) return null;

  return (
    <div className="rounded-lg ring-1 ring-white/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs font-medium text-slate-400 hover:bg-white/[0.03]"
      >
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="text-slate-500" />
        Ace&apos;s full {phaseLabel} reasoning
        {!open && interesting.length > 0 ? (
          <span className="ml-auto text-[11px] font-normal text-amber-300/90">
            {interesting.length} issue{interesting.length === 1 ? "" : "s"} — expand
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="border-t border-white/5 px-3 py-3 space-y-2">
          {summary.length > 0 ? (
            <ul className="space-y-1 text-xs text-slate-400 list-disc pl-4 mb-3">
              {summary.map((bullet, i) => (
                <li key={i}>{bullet}</li>
              ))}
            </ul>
          ) : null}
          {rows.map(({ check, human, ace, disagrees, aceFailed }) =>
            ace ? (
              <div
                key={check.id}
                className={`text-xs space-y-0.5 rounded-md px-2 py-1.5 ${
                  disagrees || aceFailed ? "bg-amber-500/[0.06] ring-1 ring-amber-500/20" : ""
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-slate-300">{check.label}</p>
                  {aceCheckDeferredToHuman(ace) ? <AceAgreedWithYouBadge compact /> : null}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-slate-400">
                  <span>
                    You:{" "}
                    <span className="font-medium text-slate-200">
                      {human ? answerLabel(human) : "—"}
                    </span>
                  </span>
                  <span>
                    Ace: <span className="font-medium text-slate-200">{answerLabel(ace.answer)}</span>
                    {aceCheckDeferredToHuman(ace) ? (
                      <span className="text-slate-500"> (mirrored)</span>
                    ) : null}
                  </span>
                </div>
                {ace.reason ? (
                  aceCheckDeferredToHuman(ace) ? (
                    <AceDeferredReason reason={ace.reason} />
                  ) : (
                    <p className="text-slate-500">{ace.reason}</p>
                  )
                ) : null}
              </div>
            ) : null,
          )}
        </div>
      ) : null}
    </div>
  );
}

export function AceComparisonPanel({
  comparison,
  loading,
  error,
  reviewId = null,
  catalogueKey = null,
  aceTrialRefreshToken = 0,
  humanPhaseRatings = null,
  onRunAce,
  onRerunAce,
  onRunFreshAce,
  onSelectAceTrial,
  onPromoteAceTrial,
  focusCheckId = null,
}: AceComparisonPanelProps) {
  const focusRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!focusCheckId || !focusRef.current) return;
    focusRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [focusCheckId, comparison?.review_id]);
  if (!comparison && !loading && !error) {
    return (
      <div className="rounded-xl bg-white/[0.03] px-4 py-4 ring-1 ring-white/5 text-center space-y-3">
        <p className="text-sm text-slate-300">Compare your blind rating with Ace</p>
        <p className="text-xs text-slate-500 max-w-sm mx-auto">
          Ace uses the same criteria and full run evidence. Your rating stays hidden from Ace until you submit.
        </p>
        <Button color="indigo" onClick={onRunAce}>
          <FontAwesomeIcon icon={faRobot} className="mr-1.5" />
          Run Ace rating
        </Button>
      </div>
    );
  }

  const findings = comparison?.ace_rating?.additional_findings ?? [];
  const critical = findings.filter((f) => f.severity === "critical");
  const notable = findings.filter((f) => f.severity === "notable");

  const phaseRows = comparison?.ace_rating
    ? Object.fromEntries(
        PHASES.map(({ key }) => [
          key,
          checksByPhase(comparison, key, humanPhaseRatings?.[key]?.answers ?? null),
        ]),
      ) as Record<ReviewPhase, ReturnType<typeof checksByPhase>>
    : null;

  const phaseRagDisagrees = (key: ReviewPhase): boolean => {
    if (!comparison) return false;
    const human = humanRagForPhase(comparison, key);
    const ace = aceRagForPhase(comparison, key);
    return Boolean(human && ace && human !== ace);
  };

  /** Prefer true check mismatches; if phase RAG disagrees but checks didn't pair, still surface Ace fails. */
  const allDisagreements = phaseRows
    ? PHASES.flatMap(({ key, label }) => {
        const rows = phaseRows[key];
        const paired = rows.filter((r) => r.disagrees && r.human && r.ace);
        if (paired.length > 0) {
          return paired.map((r) => ({ phaseLabel: label, phaseKey: key, ...r }));
        }
        if (phaseRagDisagrees(key)) {
          return rows
            .filter((r) => r.aceFailed && r.ace && (!r.human || r.human !== r.ace.answer))
            .map((r) => ({ phaseLabel: label, phaseKey: key, ...r }));
        }
        return [];
      })
    : [];

  const phaseDisagreements = comparison?.ace_rating
    ? PHASES.filter(({ key }) => phaseRagDisagrees(key))
    : [];

  const hasLoaArtefacts = (comparison?.artefacts?.length ?? 0) > 0;
  const injectedFaults = comparison?.case_context?.injected_faults ?? [];
  const runFaultMisses = injectedFaults.some(
    (fault) =>
      (fault.scope === "orchestration" || fault.scope === "output") &&
      (fault.detection.human === "missed" || fault.detection.ace === "missed"),
  );

  return (
    <div className="rounded-xl bg-white/[0.03] px-4 py-4 ring-1 ring-white/5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-200 flex items-center gap-2">
            <FontAwesomeIcon icon={faRobot} className="text-indigo-300" />
            Ace comparison
          </p>
          <p className="text-xs text-slate-500 mt-0.5">How your ratings compare to Ace&apos;s independent review</p>
          {comparison?.ace_run_number ? (
            <p className="text-[11px] text-slate-500 mt-1">
              {aceTrialRunBadge(comparison.ace_run_number)}
              {comparison.criteria_version_label ? ` · ${comparison.criteria_version_label}` : ""}
              {comparison.is_canonical === false ? " (trial)" : ""}
            </p>
          ) : comparison?.criteria_version_label ? (
            <p className="text-[11px] text-slate-500 mt-1">
              Criteria: {comparison.criteria_version_label}
              {comparison.is_canonical === false ? " (trial)" : ""}
            </p>
          ) : null}
        </div>
        {comparison?.ace_rating && onRerunAce ? (
          <button
            type="button"
            onClick={onRerunAce}
            disabled={loading}
            className="text-xs text-indigo-300 hover:text-indigo-200 disabled:opacity-50"
          >
            Re-run primary
          </button>
        ) : null}
      </div>

      {error ? <p className="text-sm text-rose-400">{error}</p> : null}
      {loading ? <p className="text-sm text-slate-400">Ace is rating this run…</p> : null}

      {comparison?.ace_rating ? (
        <>
          {comparison.case_context ? <CaseContextCard context={comparison.case_context} /> : null}

            <div className="rounded-lg bg-white/[0.02] px-3 py-3 ring-1 ring-white/5 space-y-2">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Run review</p>
            {PHASES.map(({ key, label }) => (
              <PhaseVerdictLine
                key={key}
                phaseLabel={label}
                human={humanRagForPhase(comparison, key)}
                ace={aceRagForPhase(comparison, key)}
              />
            ))}
            {phaseDisagreements.length === 0 && allDisagreements.length === 0 && !runFaultMisses ? (
              <p className="text-xs text-emerald-300/90 pt-1">You and Ace agree on every run rating.</p>
            ) : runFaultMisses ? (
              <p className="text-xs text-amber-200/90 pt-1">
                Run ratings match, but see injected faults above — at least one was missed.
              </p>
            ) : phaseDisagreements.length > 0 && allDisagreements.length > 0 ? (
              <p className="text-xs text-amber-200/90 pt-1">
                Check-by-check differences are listed below.
              </p>
            ) : null}
          </div>

          {hasLoaArtefacts ? (
            <p className="text-xs text-slate-500">
              Open the <span className="text-slate-300">Artefacts</span> tab to compare Ace&apos;s LOA
              reviews side by side with each letter.
            </p>
          ) : null}

          {allDisagreements.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-amber-300 uppercase tracking-wide">
                Where you and Ace disagree ({allDisagreements.length})
              </p>
              {allDisagreements.map(({ phaseLabel, check, human, humanComment, ace }) => (
                <div
                  key={check.id}
                  ref={check.id === focusCheckId ? focusRef : undefined}
                  className={check.id === focusCheckId ? "rounded-lg ring-2 ring-indigo-500/40" : undefined}
                >
                  <p className="text-[11px] text-slate-500 mb-1">{phaseLabel}</p>
                  <DisagreementRow
                    checkLabel={check.label}
                    human={human}
                    humanComment={humanComment}
                    aceAnswer={ace!.answer}
                    aceReason={ace!.reason}
                    deferred={aceCheckDeferredToHuman(ace)}
                  />
                </div>
              ))}
            </div>
          ) : null}

          {critical.length > 0 || notable.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Ace flagged (not in the checklist)
              </p>
              {critical.map((f, i) => (
                <div
                  key={`c-${i}`}
                  className="rounded-lg bg-rose-500/[0.08] px-3 py-2.5 ring-1 ring-rose-500/25 text-xs text-rose-100/90"
                >
                  <span className="font-medium text-rose-200">Critical: </span>
                  {f.text}
                </div>
              ))}
              {notable.map((f, i) => (
                <div
                  key={`n-${i}`}
                  className="rounded-lg bg-amber-500/[0.06] px-3 py-2.5 ring-1 ring-amber-500/20 text-xs text-amber-100/90"
                >
                  <span className="font-medium text-amber-200">Note: </span>
                  {f.text}
                </div>
              ))}
            </div>
          ) : null}

          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Ace&apos;s detail</p>
            {phaseRows
              ? PHASES.map(({ key, label }) => {
                  const aceRating = comparison.ace_rating;
                  if (!aceRating) return null;
                  const acePhase =
                    key === "orchestration" ? aceRating.orchestration : aceRating.output;
                  return (
                    <AceReasoningSection
                      key={key}
                      phaseLabel={label}
                      rows={phaseRows[key]}
                      summary={phaseSummary(acePhase)}
                      defaultOpen={phaseRagDisagrees(key) || phaseRows[key].some((r) => r.aceFailed || r.disagrees)}
                    />
                  );
                })
              : null}
          </div>
        </>
      ) : null}

      {reviewId && onRunFreshAce && onSelectAceTrial && onPromoteAceTrial ? (
        <AceFreshRunPanel
          reviewId={reviewId}
          catalogueKey={catalogueKey}
          loading={loading}
          comparison={comparison}
          activeTrialId={comparison?.trial_id ?? null}
          refreshToken={aceTrialRefreshToken}
          onRunFresh={onRunFreshAce}
          onSelectTrial={onSelectAceTrial}
          onPromoteTrial={onPromoteAceTrial}
        />
      ) : null}
    </div>
  );
}
